import { useEffect, useState } from "react";

// Whether the primary pointer can hover — `(hover: hover)`, the same media
// feature every hover rule in index.css is gated on, so a component that has
// to choose between a hover behaviour and a touch one (ZoomableImage's
// click-to-zoom against the finger's own pinch) makes the choice on the very
// condition the behaviour depends on.
//
// This is a pointer question, not a viewport one: a phone held sideways is as
// wide as a small laptop and still cannot hover, and a narrow desktop window
// still can. It is live, too — a convertible that swaps between a trackpad and
// its touch screen changes the answer without a reload.
//
// Defaults to `true` where there is no window (tests, tooling), which keeps the
// hover form — the one every existing check was written against.

const QUERY = "(hover: hover)";

function readCanHover() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return true;
  }
  return window.matchMedia(QUERY).matches;
}

function useCanHover() {
  const [canHover, setCanHover] = useState(readCanHover);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const media = window.matchMedia(QUERY);
    const update = () => setCanHover(media.matches);
    // Catch up first: the answer may have changed between render and effect.
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return canHover;
}

export default useCanHover;
